/* Measure tool readout — geodesic range and bearing.
 *
 * Uses Vincenty on the WGS84 ellipsoid, not a spherical approximation (FR-GEO-07).
 * At operational ranges the difference is metres-to-hundreds-of-metres, and this
 * number is the same kind of number a weapon range is.
 */
import { Ruler, X } from 'lucide-react'
import { useDeck } from '../state/store'
import { formatMeasure } from '../lib/geodesy'
import { formatLatLon } from '../lib/format'

export function MeasureReadout() {
  const tool = useDeck((s) => s.tool)
  const measure = useDeck((s) => s.measure)
  const clear = useDeck((s) => s.measureClear)

  if (tool !== 'measure') return null

  const { from, to } = measure
  const m = from && to ? formatMeasure(from, to) : null

  return (
    <div className="panel absolute left-3 top-3 z-20 w-56 overflow-hidden">
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <span className="title flex items-center gap-1.5">
          <Ruler size={12} aria-hidden />
          Measure
        </span>
        <button
          className="ctl"
          style={{ width: 20, height: 20, padding: 0 }}
          onClick={clear}
          aria-label="Clear measurement"
          title="Clear  (Esc)"
        >
          <X size={12} />
        </button>
      </div>

      {!from && (
        <p className="px-3 py-2" style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
          Click a start point on the map.
        </p>
      )}

      {from && !to && (
        <>
          <div className="row">
            <span className="label" style={{ fontSize: 9 }}>
              From
            </span>
            <span className="num" style={{ fontSize: 10.5 }}>
              {formatLatLon(from.lat, from.lon)}
            </span>
          </div>
          <p className="px-3 py-2" style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
            Click an end point.
          </p>
        </>
      )}

      {from && to && m && (
        <>
          <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--color-line-1)' }}>
            <div className="label" style={{ fontSize: 9 }}>
              Range
            </div>
            <div className="num" style={{ fontSize: 20, color: 'var(--color-accent-hi)' }}>
              {m.nm}
              <span className="unit" style={{ fontSize: 11 }}>
                nm
              </span>
            </div>
          </div>
          <div className="row">
            <span className="label" style={{ fontSize: 9 }}>
              Bearing
            </span>
            <span className="num" style={{ fontSize: 12 }}>
              {m.bearing}°
            </span>
          </div>
          <div className="row">
            <span className="label" style={{ fontSize: 9 }}>
              Reciprocal
            </span>
            <span className="num" style={{ fontSize: 12 }}>
              {m.reciprocal}°
            </span>
          </div>
          <div className="row">
            <span className="label" style={{ fontSize: 9 }}>
              From
            </span>
            <span className="num" style={{ fontSize: 10 }}>
              {formatLatLon(from.lat, from.lon)}
            </span>
          </div>
          <div className="row" style={{ borderBottom: 'none' }}>
            <span className="label" style={{ fontSize: 9 }}>
              To
            </span>
            <span className="num" style={{ fontSize: 10 }}>
              {formatLatLon(to.lat, to.lon)}
            </span>
          </div>
          <p className="px-3 py-1.5" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
            Geodesic (Vincenty, WGS84). Click again to start a new measurement.
          </p>
        </>
      )}
    </div>
  )
}
